/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Lock, Smartphone, ArrowRight, Loader2, ArrowLeft,
  Users as UsersIcon, Globe, CreditCard, BarChart3,
  Activity, Trash2, ChevronDown, ChevronRight, LogOut, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * CEO / Owner Portal
 * Gated by CEO_ADMIN_EMAIL + SMS 2FA (Twilio Verify). Three screens:
 *   1. anon       → ask the user to sign in
 *   2. needs_2fa  → trigger SMS + enter 6-digit code
 *   3. ceo_verified → render the portal with collapsible sections
 */
const CEO_SESSION_KEY = "iti_ceo_session_id";

function authHeaders() {
  const ceoSession = localStorage.getItem(CEO_SESSION_KEY);
  return ceoSession ? { "x-ceo-session": ceoSession } : {};
}

export default function CeoPortal() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState("loading"); // loading | anon | non_ceo | needs_2fa | ceo_verified | unconfigured
  const [phoneHint, setPhoneHint] = useState("");
  const [twilioReady, setTwilioReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/ceo/status", { headers: authHeaders() });
      setState(data.state);
      setPhoneHint(data.phone_hint || "");
      setTwilioReady(!!data.twilio_ready);
    } catch {
      setState("anon");
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  const onVerified = (sessionId) => {
    if (sessionId) localStorage.setItem(CEO_SESSION_KEY, sessionId);
    setState("ceo_verified");
  };

  const logout = async () => {
    try { await api.post("/ceo/logout", {}, { headers: authHeaders() }); } catch { /* best-effort */ }
    localStorage.removeItem(CEO_SESSION_KEY);
    setState("non_ceo");
    toast.success("Signed out of CEO portal");
  };

  if (state === "loading" || authLoading) {
    return <FullPage><Loader2 className="w-8 h-8 animate-spin" /></FullPage>;
  }

  if (state === "anon") {
    return <AnonScreen onSignIn={() => navigate("/")} />;
  }

  if (state === "non_ceo") {
    return <BlockedScreen email={user?.email} onBack={() => navigate("/dashboard")} />;
  }

  if (state === "needs_2fa") {
    return <TwoFactorScreen phoneHint={phoneHint} twilioReady={twilioReady} onVerified={onVerified} email={user?.email} />;
  }

  return <Portal user={user} onLogout={logout} />;
}

// ──────────────────────────────────────────────────────── screens

function FullPage({ children }) {
  return <div className="min-h-screen bg-[#0F0E0C] text-white flex items-center justify-center">{children}</div>;
}

function AnonScreen({ onSignIn }) {
  return (
    <FullPage>
      <div className="max-w-md w-full mx-6 bg-[#1A1815] border border-[#C9A24B]/40 rounded-2xl p-8 text-center">
        <Shield className="w-12 h-12 mx-auto mb-3 text-[#C9A24B]" />
        <div className="font-display text-3xl font-extrabold tracking-tight">CEO Portal</div>
        <div className="text-sm text-white/60 mt-2">
          Private platform-owner area. Sign in with your CEO Google account.
        </div>
        <button data-testid="ceo-signin-cta" onClick={onSignIn}
          className="mt-6 w-full py-3.5 bg-[#C9A24B] text-[#0F0E0C] font-extrabold rounded-xl inline-flex items-center justify-center gap-2">
          Go to sign in <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </FullPage>
  );
}

function BlockedScreen({ email, onBack }) {
  return (
    <FullPage>
      <div className="max-w-md w-full mx-6 bg-[#1A1815] border border-red-500/40 rounded-2xl p-8 text-center" data-testid="ceo-blocked">
        <Lock className="w-12 h-12 mx-auto mb-3 text-red-400" />
        <div className="font-display text-2xl font-extrabold">Access denied</div>
        <div className="text-sm text-white/60 mt-2">
          The CEO portal is restricted to the platform owner.
        </div>
        <div className="text-xs text-white/40 mt-1 break-all">Signed in as: {email}</div>
        <button onClick={onBack}
          className="mt-6 w-full py-3 bg-white/10 hover:bg-white/15 text-white font-bold rounded-xl">
          ← Back to dashboard
        </button>
      </div>
    </FullPage>
  );
}

function TwoFactorScreen({ phoneHint, twilioReady, onVerified, email }) {
  const [stage, setStage] = useState("intro"); // intro | sending | code
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const requestCode = async () => {
    setError(""); setStage("sending");
    try {
      const { data } = await api.post("/ceo/2fa/start", {}, { headers: authHeaders() });
      setSessionId(data.session_id);
      setStage("code");
      toast.success(`Code sent to •••• ${data.phone_hint || phoneHint}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send code";
      setError(msg);
      setStage("intro");
      toast.error(msg);
    }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/ceo/2fa/verify", { session_id: sessionId, code: code.trim() }, { headers: authHeaders() });
      onVerified(data.session_id);
      toast.success("CEO access granted");
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Invalid or expired code";
      setError(msg);
      toast.error(msg);
    }
    setSubmitting(false);
  };

  return (
    <FullPage>
      <div className="max-w-md w-full mx-6 bg-[#1A1815] border border-[#C9A24B]/40 rounded-2xl p-8" data-testid="ceo-2fa">
        <div className="flex items-center gap-2 mb-3"><Shield className="w-5 h-5 text-[#C9A24B]" /><div className="text-xs uppercase tracking-widest font-bold text-[#C9A24B]">CEO portal · 2-step verification</div></div>
        <div className="font-display text-2xl font-extrabold">Confirm it's you</div>
        <div className="text-sm text-white/60 mt-2">Signed in as <span className="text-white">{email}</span></div>

        {stage === "intro" && (
          <>
            <div className="mt-6 p-4 rounded-lg bg-black/40 border border-white/10 flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-[#C9A24B] shrink-0" />
              <div className="text-sm">
                We'll text a 6-digit code to your phone ending in <span className="font-bold text-white">{phoneHint || "••••"}</span>.
              </div>
            </div>
            {!twilioReady && (
              <div className="mt-3 text-xs text-amber-300 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  SMS is not configured yet. Add <code className="text-white">TWILIO_ACCOUNT_SID</code>, <code className="text-white">TWILIO_AUTH_TOKEN</code>, and <code className="text-white">TWILIO_VERIFY_SERVICE_SID</code> to backend env.
                </div>
              </div>
            )}
            <button onClick={requestCode} disabled={!twilioReady}
              data-testid="ceo-send-code-btn"
              className="mt-6 w-full py-3.5 bg-[#C9A24B] text-[#0F0E0C] font-extrabold rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-40">
              Send verification code <ArrowRight className="w-4 h-4" />
            </button>
            {error && <div className="mt-3 text-xs text-red-300">{error}</div>}
          </>
        )}

        {stage === "sending" && (
          <div className="mt-8 text-center text-sm text-white/60 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Sending code…
          </div>
        )}

        {stage === "code" && (
          <form onSubmit={submitCode} className="mt-5 space-y-3" data-testid="ceo-2fa-form">
            <label className="block">
              <div className="text-xs uppercase font-bold text-white/60 mb-1">Enter the 6-digit code</div>
              <input
                autoFocus inputMode="numeric" pattern="\d*" maxLength={10}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-2xl tracking-widest text-center font-mono"
                data-testid="ceo-2fa-code"
              />
            </label>
            <button type="submit" disabled={submitting || code.length < 4}
              className="w-full py-3.5 bg-[#C9A24B] text-[#0F0E0C] font-extrabold rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="ceo-2fa-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enter portal"}
            </button>
            {error && <div className="text-xs text-red-300">{error}</div>}
            <button type="button" onClick={requestCode}
              className="block w-full text-center text-xs text-white/50 hover:text-white/80 underline">
              Resend code
            </button>
          </form>
        )}
      </div>
    </FullPage>
  );
}

// ──────────────────────────────────────────────────────── portal

function Portal({ user, onLogout }) {
  return (
    <div className="min-h-screen bg-[#0F0E0C] text-white" data-testid="ceo-portal">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 bg-[#0F0E0C]/95 backdrop-blur z-10">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-[#C9A24B]" />
          <div>
            <div className="font-display text-xl font-extrabold leading-none">CEO Portal</div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Interest To Income · Owner Console</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-white/60 hidden md:block">{user?.email}</div>
          <button onClick={onLogout} data-testid="ceo-logout-btn"
            className="text-xs font-bold inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 px-3 py-1.5 rounded-lg">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-3">
        <Section id="overview" title="Platform Overview" icon={BarChart3} defaultOpen><OverviewBlock /></Section>
        <Section id="users" title="Users & Accounts" icon={UsersIcon}><UsersBlock /></Section>
        <Section id="websites" title="Websites" icon={Globe}><WebsitesBlock /></Section>
        <Section id="subs" title="Subscriptions" icon={CreditCard}><SubscriptionsBlock /></Section>
        <Section id="fin" title="Revenue & Financials" icon={CreditCard}><FinancialsBlock /></Section>
        <Section id="domains" title="Domains" icon={Globe}><DomainsBlock /></Section>
        <Section id="audit" title="Activity Log" icon={Activity}><AuditBlock /></Section>
      </main>
    </div>
  );
}

function Section({ id, title, icon: Icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl bg-[#161412]" data-testid={`ceo-section-${id}`}>
      <button onClick={() => setOpen((x) => !x)} data-testid={`ceo-section-toggle-${id}`}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#C9A24B]/10 text-[#C9A24B]"><Icon className="w-4 h-4" /></div>
          <div className="font-display text-base font-extrabold">{title}</div>
        </div>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────── data blocks

function OverviewBlock() {
  const { data, loading, error } = useFetch("/ceo/overview");
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox msg={error} />;
  const cards = [
    { label: "Total users", value: data.users_total },
    { label: "Total websites", value: data.websites_total },
    { label: "Published", value: data.websites_published },
    { label: "Custom domains", value: data.websites_with_custom_domain },
    { label: "Free subdomains", value: data.websites_on_free_subdomain },
    { label: "Paid active", value: data.users_paid_active },
    { label: "Trialing", value: data.users_trialing },
    { label: "Past due", value: data.users_past_due },
    { label: "Canceled", value: data.users_canceled },
    { label: "Signup → paid", value: `${data.signup_to_paid_rate_pct}%` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ceo-overview-grid">
      {cards.map((c) => (
        <div key={c.label} className="bg-black/40 rounded-lg p-3 border border-white/5">
          <div className="text-[10px] uppercase text-white/40">{c.label}</div>
          <div className="text-2xl font-extrabold mt-1">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function UsersBlock() {
  const [q, setQ] = useState("");
  const { data, loading, error, refresh } = useFetch(`/ceo/users?q=${encodeURIComponent(q)}`);
  // Hide the Delete action on the operator's own row — backend also rejects this with 400,
  // but pre-empting it in the UI keeps the action list clean.
  const ceoId = "user_ceo_owner_001";
  const onDelete = async (u) => {
    if (!window.confirm(`Soft-delete ${u.email}? Customer will lose access. Restorable for 30 days.`)) return;
    try {
      await api.delete(`/ceo/users/${u.user_id}`, { headers: authHeaders() });
      toast.success("User soft-deleted");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  return (
    <div className="space-y-3">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…"
        className="w-full md:w-80 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm" data-testid="ceo-users-search" />
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-white/40 border-b border-white/10">
            <tr><th className="text-left py-2 px-2">Email</th><th className="text-left">Plan</th><th className="text-left">Status</th><th className="text-left">Sites</th><th className="text-left">Created</th><th></th></tr>
          </thead>
          <tbody>
            {(data?.users || []).map((u) => (
              <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`ceo-user-row-${u.user_id}`}>
                <td className="py-2 px-2">{u.email}{u.disabled_at && <span className="ml-2 text-[10px] text-red-300">DISABLED</span>}</td>
                <td>{u.plan_id || "free"}</td>
                <td>{u.plan_status || "—"}</td>
                <td>{u.business_count || 0}</td>
                <td className="text-white/50">{(u.created_at || "").slice(0, 10)}</td>
                <td className="text-right pr-2">
                  {!u.disabled_at && u.user_id !== ceoId && (
                    <button onClick={() => onDelete(u)} className="text-red-300 hover:text-red-200 text-xs inline-flex items-center gap-1" data-testid={`ceo-user-del-${u.user_id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                  {u.user_id === ceoId && (
                    <span className="text-[10px] text-[#C9A24B] uppercase tracking-wider" data-testid="ceo-self-marker">You · CEO</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.users || data.users.length === 0) && <div className="py-6 text-center text-white/40 text-sm">No users found</div>}
      </div>
    </div>
  );
}

function WebsitesBlock() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const { data, loading, error, refresh } = useFetch(`/ceo/websites?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}`);
  const onDelete = async (w) => {
    if (!window.confirm(`Soft-delete website /${w.slug}? Restorable for 30 days.`)) return;
    try {
      await api.delete(`/ceo/websites/${w.business_id}`, { headers: authHeaders() });
      toast.success("Website soft-deleted");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEditor = (w) => {
    // Open the dashboard's pages tab with the impersonated business id — backend will reject unless
    // the regular session has access. For pure read/view, the public URL also works.
    window.open(`/site/${w.slug}`, "_blank");
  };
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="slug, name, or domain…"
          className="flex-1 md:max-w-80 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm" data-testid="ceo-websites-search" />
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-white/40 border-b border-white/10">
            <tr><th className="text-left py-2 px-2">Site</th><th className="text-left">Owner</th><th className="text-left">Status</th><th className="text-left">Domain</th><th className="text-left">Created</th><th></th></tr>
          </thead>
          <tbody>
            {(data?.websites || []).map((w) => (
              <tr key={w.business_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`ceo-website-row-${w.business_id}`}>
                <td className="py-2 px-2">
                  <div className="font-bold">{w.summary?.name || w.slug}</div>
                  <div className="text-[10px] text-white/40">/{w.slug}</div>
                </td>
                <td className="text-white/70">{w.owner_email || "—"}</td>
                <td>{w.status}</td>
                <td>{w.custom_domain || "—"}</td>
                <td className="text-white/50">{(w.created_at || "").slice(0, 10)}</td>
                <td className="text-right pr-2 whitespace-nowrap">
                  <button onClick={() => openEditor(w)} className="text-xs underline mr-2" data-testid={`ceo-website-open-${w.business_id}`}>Open</button>
                  {w.status !== "disabled" && (
                    <button onClick={() => onDelete(w)} className="text-red-300 hover:text-red-200 text-xs inline-flex items-center gap-1" data-testid={`ceo-website-del-${w.business_id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data?.websites || data.websites.length === 0) && <div className="py-6 text-center text-white/40 text-sm">No websites found</div>}
      </div>
    </div>
  );
}

function SubscriptionsBlock() {
  const { data, loading, error } = useFetch("/ceo/subscriptions");
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  const buckets = [
    { id: "active", label: "Active", users: data?.active || [] },
    { id: "trialing", label: "Trialing", users: data?.trialing || [] },
    { id: "past_due", label: "Past due", users: data?.past_due || [] },
    { id: "canceled", label: "Canceled", users: data?.canceled || [] },
    { id: "free", label: "Free", users: data?.free || [] },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-2 text-center text-sm">
        {buckets.map((b) => (
          <div key={b.id} className="bg-black/40 rounded-lg p-3 border border-white/5">
            <div className="text-[10px] uppercase text-white/40">{b.label}</div>
            <div className="text-2xl font-extrabold mt-1">{b.users.length}</div>
          </div>
        ))}
      </div>
      {buckets.filter((b) => b.users.length).map((b) => (
        <details key={b.id} className="bg-black/30 rounded-lg p-3 border border-white/5">
          <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-white/70">{b.label} ({b.users.length})</summary>
          <ul className="mt-2 text-xs space-y-1">
            {b.users.map((u) => <li key={u.user_id} className="flex justify-between"><span>{u.email}</span><span className="text-white/40">{u.plan_id} · until {u.plan_until || "—"}</span></li>)}
          </ul>
        </details>
      ))}
    </div>
  );
}

function FinancialsBlock() {
  const { data, loading, error } = useFetch("/ceo/financials");
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  const big = [
    { label: "MRR", value: `$${data?.mrr_usd ?? 0}` },
    { label: "ARR", value: `$${data?.arr_usd ?? 0}` },
    { label: "Active subscribers", value: data?.subscribers_count ?? 0 },
  ];
  const minor = [
    { label: "Extra-domain MRR", value: `$${data?.extra_domain_mrr_usd ?? 0}` },
    { label: "Paid (last 30d)", value: `$${data?.paid_last_30d_usd ?? 0}` },
    { label: "Refunded (last 30d)", value: `$${data?.refunded_last_30d_usd ?? 0}` },
    { label: "Failed payments (30d)", value: data?.failed_payments_30d ?? 0 },
    { label: "Past-due users", value: data?.users_past_due ?? 0 },
    { label: "Canceled users", value: data?.users_canceled ?? 0 },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {big.map((c) => (
          <div key={c.label} className="bg-[#C9A24B]/10 border border-[#C9A24B]/30 rounded-lg p-4">
            <div className="text-[10px] uppercase text-[#C9A24B]">{c.label}</div>
            <div className="text-3xl font-extrabold mt-1">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {minor.map((c) => (
          <div key={c.label} className="bg-black/40 rounded-lg p-3 border border-white/5">
            <div className="text-[10px] uppercase text-white/40">{c.label}</div>
            <div className="text-xl font-extrabold mt-1">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-white/40 italic">
        MRR is computed from active users × plan price. For accounting truth, check the Stripe dashboard.
      </div>
    </div>
  );
}

function DomainsBlock() {
  const { data, loading, error } = useFetch("/ceo/domains");
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  return (
    <div className="space-y-3">
      <div className="text-xs text-white/60">
        {data?.extra_subs_active || 0} active extra-domain subscriptions · {data?.extra_subs_total || 0} total
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase text-white/40 border-b border-white/10">
          <tr><th className="text-left py-2">Site</th><th className="text-left">Owner</th><th className="text-left">Domain</th><th className="text-left">Status</th></tr>
        </thead>
        <tbody>
          {(data?.domains || []).map((d) => (
            <tr key={d.business_id} className="border-b border-white/5">
              <td className="py-2">{d.summary?.name || d.slug}</td>
              <td className="text-white/70">{d.user_id}</td>
              <td>{d.custom_domain}</td>
              <td>{d.domain_status || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {(!data?.domains || data.domains.length === 0) && <div className="py-4 text-center text-white/40 text-sm">No custom domains configured yet</div>}
    </div>
  );
}

function AuditBlock() {
  const { data, loading, error } = useFetch("/ceo/audit?limit=100");
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  return (
    <div className="space-y-1 text-xs font-mono max-h-96 overflow-y-auto">
      {(data?.audit || []).map((row) => (
        <div key={row.audit_id} className="flex gap-3 border-b border-white/5 py-1.5">
          <span className="text-white/40 shrink-0">{(row.created_at || "").slice(0, 19).replace("T", " ")}</span>
          <span className="text-[#C9A24B] shrink-0">{row.action}</span>
          <span className="text-white/70 truncate">{JSON.stringify(row.target)}</span>
        </div>
      ))}
      {(!data?.audit || data.audit.length === 0) && <div className="py-4 text-center text-white/40 text-sm">No activity yet</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────── helpers

function Loading() { return <div className="py-6 text-center text-white/40 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>; }
function ErrorBox({ msg }) { return <div className="py-4 text-center text-red-300 text-sm">{msg || "Could not load"}</div>; }

function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get(url, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string" ? e.response.data.detail : (e?.message || "Network error");
      setError(msg);
    }
    setLoading(false);
  }, [url]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, error, refresh: reload };
}
